import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { NbDialogService } from '@nebular/theme';
import { environment } from '../../environments/environment';
import { SelectDialogComponent } from '../@theme/components/select-dialog/select-dialog.component';
import { AuthService } from './auth.service';
import { ExerciseOptionsService } from './exercise-options.service';

/**
 * The shared post-login sequence (#80): coach-exercise preload → school-selection
 * dialog → parent-info check → routing.
 *
 * Extracted from <c>LoginComponent.login()</c> so the SSO return path
 * (<see cref="SsoService"/>) re-enters the EXACT same flow a password login does.
 * Without this the school-selection dialog — which lived only inside the login
 * component — would be silently skipped for SSO users, breaking parity. Callers
 * must have stored the access (and refresh) token before calling <c>run()</c>.
 */
@Injectable({ providedIn: 'root' })
export class PostLoginService {
  private DEFAULT_LOGIN_DEST = '/calendar';

  constructor(
    private authService: AuthService,
    private exerciseOptionsService: ExerciseOptionsService,
    private router: Router,
    private dialogService: NbDialogService,
    private http: HttpClient) { }

  /**
   * Run the sequence. <c>dismissFallbackUrl</c> is where to send the user if they
   * close the school-selection dialog without choosing: callers whose own page is
   * still on screen (the login form) pass nothing and the dismissal is simply a
   * no-op, but the SSO return leg is sitting on #/auth/sso-return — a spinner that
   * is never a resting state — so it passes a real page to fall back to, otherwise
   * a dismissed dialog leaves the user watching "Signing you in" forever (#80).
   *
   * Passing it also marks the caller as one with nowhere of its own to sit, so the
   * OTHER outcome that would silently stop — the subscriptions fetch failing — sends
   * them to the calendar instead. Both leaks strand the same spinner, so they have
   * to be closed together for SsoReturnComponent's "every outcome navigates away"
   * to actually hold.
   */
  run(dismissFallbackUrl?: string): void {
    this.exerciseOptionsService.loadExercisesForCoach();
    const userData = this.authService.getUserData();

    this.authService.getAvailableSubscriptions().subscribe((subs) => {
      const selectItems = subs.AvailableSubscriptions.map((s) => {
        return {
          image: s.ImageUrl,
          banner: s.BannerUrl,
          title: s.DisplayName,
          id: s.SchoolId,
        };
      });

      if (userData.needsSchoolSelection) {
        this.dialogService.open(SelectDialogComponent, {
          context: { items: selectItems, showImage: true, heading: 'Select Subscription' },
        }).onClose.subscribe((selected) => {
          if (!selected) {
            // Dialog dismissed without a choice. Callers that left a visible page
            // behind want nothing to happen; callers parked on a spinner have to be
            // sent somewhere real (see the doc comment).
            if (dismissFallbackUrl) {
              this.router.navigateByUrl(dismissFallbackUrl);
            }
            return;
          }
          this.authService.selectSchoolAndUpdateToken(
            selected.id, selected.title, selected.image, selected.banner).subscribe(
              (token: string) => {
                this.authService.storeToken(token);
                this.checkParentInfoAndRedirect();
              },
              () => this.router.navigate(['/calendar']), // fall back on token-exchange error
            );
        });
      } else {
        window.localStorage.setItem('school-id', selectItems[0]?.id);
        window.localStorage.setItem('school-name', selectItems[0]?.title);
        window.localStorage.setItem('school-image', selectItems[0]?.image);
        window.localStorage.setItem('school-banner', selectItems[0]?.banner);
        this.checkParentInfoAndRedirect();
      }
    }, () => {
      // The fetch failed, but the token is already stored — the user IS signed in,
      // so there is nothing to retry and no error to show them. Send the parked
      // callers on to their default destination, the same way
      // checkParentInfoAndRedirect falls through on its own error rather than
      // leaving the sequence with no outcome.
      if (dismissFallbackUrl) {
        this.router.navigateByUrl(this.DEFAULT_LOGIN_DEST);
      }
    });
  }

  /**
   * Athletes without parent info are routed to the parent-info page (and flagged for
   * ParentInfoGuard); everyone else goes to their default destination. Any error
   * falls through to the calendar rather than stranding the user.
   */
  private checkParentInfoAndRedirect(): void {
    if (!this.authService.isAthlete()) {
      this.router.navigateByUrl(this.DEFAULT_LOGIN_DEST);
      return;
    }

    this.http.get(`${environment.baseUrl}/api/RackInsightsApi/CheckParentInfo`).subscribe(
      (data: any) => {
        if (!data.hasParentInfo) {
          localStorage.setItem('needs-parent-info', 'true');
          this.router.navigate(['/parent-info'], { replaceUrl: true });
        } else {
          localStorage.removeItem('needs-parent-info');
          this.router.navigate(['/calendar']);
        }
      },
      () => this.router.navigate(['/calendar']),
    );
  }
}
